import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../shared/components/Button';
import { ButtonsGroup } from '../../shared/components/ButtonsGroup';
import { Radio } from '../../shared/components/Radio';
import { TitleAndSubtitle } from '../../shared/components/TitleAndSubtitle';
import { AuthStatus, useAuth } from '../../shared/hooks/Auth';
import { ROUTES } from '../../shared/routing/routes';
import { useOnboardingStore } from './hooks/useOnboardingStore';
import { OllieFloating } from '../../shared/components/OllieFloating';
import logoText from '../../shared/assets/img/logo-text.svg';

const options = [
    {
        label: 'Have my thoughts bother me less',
        value: 'thoughts',
    },
    {
        label: 'Reduce my compulsions',
        value: 'compulsion',
    },
    {
        label: 'Feel less anxious',
        value: 'anxiety',
    },
    {
        label: 'Get my time and energy back',
        value: 'energy',
    },
    {
        label: 'Understand my OCD better',
        value: 'understand',
    },
    {
        label: 'Something else',
        value: 'other',
    }
]

export const WhatBringsYou = () => {
    const navigate = useNavigate();
    const { authStatus, signInAnonymously } = useAuth();
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const setCurrentStep = useOnboardingStore(state => state.setCurrentStep);
    const setWhatBringsYouResponse = useOnboardingStore(state => state.setWhatBringsYouResponse);

    useEffect(() => {
        setCurrentStep(2);
    }, [setCurrentStep]);

    // Navigate to DrawAHeart once background auth reaches AuthenticatedReady
    useEffect(() => {
        if (isAuthenticating && authStatus === AuthStatus.AuthenticatedReady) {
            setIsAuthenticating(false);
            navigate(ROUTES.onboarding.drawHeart);
        }
    }, [isAuthenticating, authStatus, navigate]);

    const handleSelect = (id: string) => {
        setSelectedOptions(prev => {
            if (prev.includes(id)) {
                return prev.filter(option => option !== id);
            }

            if (prev.length >= 2) {
                return prev;
            }

            return [...prev, id];
        });
    }

    const handleContinue = async () => {
        setWhatBringsYouResponse(selectedOptions);

        if (authStatus === AuthStatus.Unauthenticated) {
            setIsAuthenticating(true);
            try {
                await signInAnonymously();
            } catch (error) {
                console.error('Failed to sign in anonymously:', error);
                setIsAuthenticating(false);
            }
            return;
        }

        navigate(ROUTES.onboarding.drawHeart);
    }

    return (
        <div className='flex flex-col gap-4 safe-pb-4 px-8 flex-1 overflow-hidden'>
            <title>Biggest OCD Pain?</title>
            <div className='flex-shrink-0 relative'>
                <div className='flex justify-center items-center'>
                    <img src={logoText} alt="logo" className='h-5' />
                </div>
                <OllieFloating size='small' />
                <TitleAndSubtitle
                    title='What do you want to work on first with your OCD?'
                    subtitle='Pick 1 or 2 that matter most to you.' />
            </div>
            <div className='flex-1 overflow-y-auto py-4'>
                <Radio options={options.map((option) => ({
                    id: option.value,
                    label: option.label,
                    state: selectedOptions.includes(option.value) ? 'selected' : 'notSelected',
                }))}
                    onSelect={handleSelect} />
            </div>
            <ButtonsGroup animated={false}>
                <Button label='This!' inactive={selectedOptions.length < 1} isLoading={isAuthenticating} onClick={handleContinue} />
            </ButtonsGroup>
        </div>
    );
};
